<template>
  <div class="section-container">
    <div class="section-header">
      <h1 class="section-title">考試管理</h1>
      <div class="table-controls">
        <div class="search-add-group">
          <input
            type="text"
            v-model="filterText"
            placeholder="搜索考試..."
            class="search-input"
          />
          <button class="button button-primary" @click="openModal(null)">新增考試</button>
        </div>
      </div>
    </div>

    <div class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th @click="sortBy('exam_id')">
              ID
              <span v-if="sortColumn === 'exam_id'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('name')">
              考試名稱
              <span v-if="sortColumn === 'name'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('authority')">
              考試機構
              <span v-if="sortColumn === 'authority'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('type')">
              考試類型
              <span v-if="sortColumn === 'type'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('subject')">
              科目
              <span v-if="sortColumn === 'subject'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('grade')">
              年級
              <span v-if="sortColumn === 'grade'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('exam_date')">
              考試日期
              <span v-if="sortColumn === 'exam_date'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('full_score')">
              總分
              <span v-if="sortColumn === 'full_score'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="exam in sortedAndFilteredExams" 
            :key="exam.exam_id"
            @click="handleRowClick(exam.exam_id, $event)"
            class="clickable-row"
          >
            <td>{{ exam.exam_id }}</td>
            <td>{{ exam.name }}</td>
            <td>
              <span :class="`authority-badge ${exam.authority.toLowerCase()}`">
                {{ getAuthorityLabel(exam.authority) }}
              </span>
            </td>
            <td>
              <span class="type-tag">{{ getTypeLabel(exam.type) }}</span>
            </td>
            <td>
              <span class="subject-tag">{{ getSubjectLabel(exam.subject) }}</span>
            </td>
            <td>
              <span class="grade-tag">{{ getGradeLabel(exam.grade) }}</span>
            </td>
            <td>{{ formatDate(exam.exam_date) }}</td>
            <td>{{ exam.full_score || '-' }}</td>
            <td class="actions" @click.stop>
              <button class="button button-secondary" @click="openModal(exam)">編輯</button>
              <button class="button button-danger" @click="confirmDelete(exam.exam_id)">刪除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ExamModal
      v-if="showModal"
      :exam="currentExam"
      :isEdit="isEditMode"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery, useMutation } from '@vue/apollo-composable';
import { GET_EXAMS, DELETE_EXAM } from '../graphql/queries';
import ExamModal from './ExamModal.vue';

const router = useRouter();
const { result, refetch } = useQuery(GET_EXAMS);
const { mutate: deleteExam } = useMutation(DELETE_EXAM);

// Modal state
const showModal = ref(false);
const currentExam = ref(null);
const isEditMode = ref(false);

// Sorting state
const sortColumn = ref('exam_id');
const sortDirection = ref('asc');

// Filter state
const filterText = ref('');

// Computed properties
const exams = computed(() => result.value?.exams || []);

// Combined sorted and filtered exams
const sortedAndFilteredExams = computed(() => {
  let filtered = exams.value;
  
  // Apply filtering if search text exists
  const searchText = filterText.value.trim().toLowerCase();
  if (searchText) {
    filtered = filtered.filter(exam =>
      exam.name.toLowerCase().includes(searchText) ||
      getAuthorityLabel(exam.authority).toLowerCase().includes(searchText) ||
      getTypeLabel(exam.type).toLowerCase().includes(searchText) ||
      getSubjectLabel(exam.subject).toLowerCase().includes(searchText) ||
      getGradeLabel(exam.grade).toLowerCase().includes(searchText) ||
      exam.exam_id.toString().includes(searchText)
    );
  }
  
  // Apply sorting
  return [...filtered].sort((a, b) => {
    let valA, valB;
    
    switch (sortColumn.value) {
      case 'exam_id':
        valA = parseInt(a.exam_id);
        valB = parseInt(b.exam_id);
        break;
      case 'exam_date':
        valA = a.exam_date ? new Date(a.exam_date) : null;
        valB = b.exam_date ? new Date(b.exam_date) : null;
        break;
      case 'authority':
        valA = getAuthorityLabel(a.authority);
        valB = getAuthorityLabel(b.authority);
        break;
      case 'type':
        valA = getTypeLabel(a.type);
        valB = getTypeLabel(b.type);
        break;
      case 'subject':
        valA = getSubjectLabel(a.subject);
        valB = getSubjectLabel(b.subject);
        break;
      case 'grade':
        valA = a.grade || 0;
        valB = b.grade || 0;
        break;
      default:
        valA = a[sortColumn.value];
        valB = b[sortColumn.value];
    }
    
    // Handle null values for sorting
    if (valA === null || valA === undefined) return 1;
    if (valB === null || valB === undefined) return -1;
    
    if (typeof valA === 'string') {
      valA = valA.toLowerCase();
      valB = valB.toLowerCase();
    }
    
    if (valA < valB) {
      return sortDirection.value === 'asc' ? -1 : 1;
    }
    if (valA > valB) {
      return sortDirection.value === 'asc' ? 1 : -1;
    }
    return 0;
  });
});

// Labels for enums
const authorityLabels = {
  SCHOOL: '學校',
  MACADEMY: '補習班',
  OTHER: '其他'
};

const typeLabels = {
  IDTERM: '期中考',
  FINAL: '期末考',
  WEEKLY: '週考',
  FIRST_TERM: '第一次段考',
  SECOND_TERM: '第二次段考',
  THIRD_TERM: '第三次段考',
  QUIZ: '隨堂考',
  MOCK: '模擬考',
  MONTHLY: '月考',
  UNIT_TEST: '單元測驗',
  REVIEW_TEST: '複習考',
  PLACEMENT: '分班測驗',
  CERTIFICATION: '證照考試',
  COMPETITION: '競賽'
};

const subjectLabels = {
  CHINESE: '國文',
  CHINESE_ESSAY: '國文作文',
  ENGLISH: '英文',
  ENGLISH_ESSAY: '英文作文',
  ENGLISH_LISTENING: '英文聽力',
  MATH: '數學',
  BIOLOGY: '生物',
  SCIENCE: '理化',
  PHYSICS: '物理',
  CHEMISTRY: '化學',
  HISTORY: '歷史',
  GEOGRAPHY: '地理',
  CIVICS: '公民',
  OTHER: '其他'
};

const getGradeLabels = {
  1: '小一',
  2: '小二',
  3: '小三',
  4: '小四',
  5: '小五',
  6: '小六',
  7: '國一',
  8: '國二',
  9: '國三',
  10: '高一',
  11: '高二',
  12: '高三'
}

// Helper functions
function getAuthorityLabel(authority) {
  return authorityLabels[authority] || authority;
}

function getTypeLabel(type) {
  return typeLabels[type] || type;
}

function getSubjectLabel(subject) {
  return subjectLabels[subject] || subject;
}

function getGradeLabel(grade) {
  return getGradeLabels[grade] || (grade ? ` ${grade}年級` : '-');
}

function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('zh-TW');
}

function sortBy(column) {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = column;
    sortDirection.value = 'asc';
  }
}

function handleRowClick(examId, event) {
  const isButtonClick = event.target.closest('button') !== null;
  if (!isButtonClick) {
    router.push(`/exams/${examId}`);
  }
}

function openModal(exam) {
  currentExam.value = exam;
  isEditMode.value = !!exam;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

function handleSaved() {
  refetch();
  closeModal();
}

function confirmDelete(examId) {
  if (confirm('确定要删除这个考试吗？此操作不可撤销。')) {
    deleteExam({ exam_id: examId }).then(() => {
      refetch();
    });
  }
}
</script>

<style scoped>
.table-container {
  overflow-x: auto;
}

.authority-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  color: white;
}

.authority-badge.school {
  background-color: var(--primary-color);
}

.authority-badge.macademy {
  background-color: var(--secondary-color);
}

.authority-badge.other {
  background-color: var(--text-light);
}

.type-tag, .subject-tag .grade-tag {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  background-color: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
}

.grade-tag {
  background-color: #e8f5e8;
  color: #2e7d32;
  border-color: #c8e6c9;
}

.actions {
  display: flex;
  gap: 8px;
  cursor: default;
}

.button-danger {
  background-color: var(--danger-color);
  color: white;
}

.button-danger:hover {
  background-color: var(--danger-hover);
}

/* Sorting styles */
th {
  cursor: pointer;
  position: relative;
  user-select: none;
}

th:hover {
  background-color: var(--light-bg);
}

.sort-icon {
  margin-left: 4px;
  font-size: 0.8em;
}

.table-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.search-input {
  padding: 0.5rem 1rem;
  height: 32px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.9rem;
  width: 220px;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.search-add-group {
  display: flex;
  align-items: center;
  gap: 0;
}

.search-add-group .search-input {
  border-radius: 4px 0 0 4px;
  border-right: none;
}

.search-add-group .button {
  border-radius: 0 4px 4px 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-controls {
    flex-direction: column;
    align-items: flex-end;
    gap: 0.75rem;
    width: 100%;
  }
  
  .search-add-group {
    width: 100%;
  }
  
  .search-add-group .search-input {
    flex-grow: 1;
    width: auto;
  }
}
</style>